import { Box, CircularProgress, Grid, Stack, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import { RenderStates } from "../../atoms/RenderStates/RenderStates";
import { OrganizationItemWithAvatar } from "../../organisms/Organizations/OrganizationItemWithAvatar";
import { type Organization } from "../../organisms/Organizations/types";
import { NavLinkWrapper } from "./NavLinkWrapper";

export interface AdminOrganizationsTemplateProps {
  data?: Organization[];
  status: Utils.QueryStatus;
  showHeader?: boolean;
  isSiteAdmin?: boolean;
  linkTo?: (organization: Organization) => string;
}

function SearchHeader({ searchTerm, onSearchChange }: { searchTerm: string; onSearchChange: (value: string) => void }) {
  return (
    <Box>
      <Typography variant="h5">Organizations</Typography>
      <TextField
        fullWidth
        label="Search Organizations"
        onChange={(e) => onSearchChange(e.target.value)}
        size="small"
        value={searchTerm}
      />
    </Box>
  );
}

function OrganizationsList({
  organizations,
  isSiteAdmin,
  linkTo,
}: {
  organizations: Organization[];
  isSiteAdmin: boolean;
  linkTo?: (organization: Organization) => string;
}) {
  return (
    <Grid container role="list" spacing={2}>
      {organizations.map((organization) => (
        <Grid key={organization.id} role="listitem" size={12}>
          {linkTo ? (
            <NavLinkWrapper to={linkTo(organization)}>
              <OrganizationItemWithAvatar isSiteAdmin={isSiteAdmin} organization={organization} />
            </NavLinkWrapper>
          ) : (
            <OrganizationItemWithAvatar isSiteAdmin={isSiteAdmin} organization={organization} />
          )}
        </Grid>
      ))}
    </Grid>
  );
}

export const AdminOrganizationsTemplate = ({
  data: organizations,
  status,
  showHeader = true,
  isSiteAdmin = false,
  linkTo,
}: AdminOrganizationsTemplateProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOrganizations = useMemo(() => {
    if (!organizations) {
      return [];
    }
    return organizations.filter((org) => org.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [organizations, searchTerm]);

  return (
    <Box>
      <Stack spacing={3}>
        {showHeader && <SearchHeader onSearchChange={setSearchTerm} searchTerm={searchTerm} />}
        <RenderStates
          data={filteredOrganizations}
          empty={<Typography>No organizations found</Typography>}
          error={<Typography color="error">Error loading organizations</Typography>}
          pending={<CircularProgress />}
          status={status}
          success={(orgs) => <OrganizationsList isSiteAdmin={isSiteAdmin} linkTo={linkTo} organizations={orgs} />}
        />
      </Stack>
    </Box>
  );
};
